import { CircleAlert, Download, FlaskConical } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { PageTitle } from '@/app/layout'
import { BarList, Donut, DonutLegend, Legend, MovementChart, StackedBars } from '@/components/redesign/charts'
import { CountryCode, KpiTile, Note, Panel, SectionHead, Tabs, Track } from '@/components/redesign/bits'
import { Button } from '@/components/ui/button'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import type { Sales } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { formatDate, formatMoney, formatUsd, numberFmt } from '@/lib/format'
import { useSales } from './api'
import { label } from './format'

const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit' })
const monthLabel = (ym: string) => monthFmt.format(new Date(`${ym}-01T00:00:00`))
const longMonth = (ym: string) =>
  new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(`${ym}-01T00:00:00`))

const RANGES = [
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 24, label: '2 years' },
  { value: 36, label: '3 years' },
]

// Each plan keeps its colour whichever plans have subscriptions (as on PlanTag).
const PLAN_COLORS: Record<string, string> = {
  professional: 'var(--viz-1)',
  business: 'var(--viz-2)',
  starter: 'var(--viz-3)',
}
const MARKETS = {
  tier1: { label: 'Tier 1', color: 'var(--viz-1)' },
  hubs: { label: 'Hubs', color: 'var(--viz-2)' },
  other: { label: 'Elsewhere', color: 'var(--viz-other)' },
} as const
const ENDINGS = {
  owner: 'Cancelled by the customer',
  outside: 'Cancelled in Lemon Squeezy',
  staff: 'Cancelled by the team',
  payment_failed: 'Payment kept failing',
  refund: 'Refunded',
} as const

type PayFilter = 'all' | 'paid' | 'failed' | 'refunded'
const PAY_FILTERS: { value: PayFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
]
const payMatches = (status: string, f: PayFilter) =>
  f === 'all' ||
  (f === 'paid' && status === 'paid') ||
  (f === 'refunded' && (status === 'refunded' || status === 'partial_refund')) ||
  (f === 'failed' && status !== 'paid' && status !== 'refunded' && status !== 'partial_refund')

const pct = (v: number | null | undefined, digits = 0) => (v == null ? '—' : `${v.toFixed(digits)}%`)

/**
 * Revenue (redesign of 2026-10-16): recurring revenue and how it holds up, this month's movement,
 * revenue by plan, plan mix, yearly billing, add-ons and markets, how plans end, every payment,
 * and the month-by-month tables. Opening it is recorded in the audit log.
 */
export function SalesPage() {
  const [range, setRange] = useState(12)
  const sales = useSales(range)

  return (
    <>
      <PageTitle
        title="Revenue"
        description="Recurring revenue, where it moves, and every payment as Lemon Squeezy reports it. Each subscription counts at what it pays: its latest regular payment before tax and after discounts. Opening this page is recorded in the audit log."
        actions={<Segmented label="Period" value={range} options={RANGES} onChange={setRange} />}
      />
      {sales.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : sales.isError ? (
        <div className="card">
          <ErrorState error={sales.error} onRetry={() => void sales.refetch()} />
        </div>
      ) : (
        <Body s={sales.data} dim={sales.isPlaceholderData} />
      )}
    </>
  )
}

function Body({ s, dim }: { s: Sales; dim: boolean }) {
  const months = s.history.months
  const latest = months.at(-1)
  const before = months.at(-2)
  const arpaBefore = before && before.customers > 0 ? Math.round(before.mrr / before.customers) : null
  const compare = 'vs last month'
  const planKeys = [...new Set(s.plans.map((p) => p.plan))].map((key) => ({
    key,
    label: s.plans.find((p) => p.plan === key)?.name ?? key,
    color: PLAN_COLORS[key] ?? 'var(--viz-other)',
  }))
  const stackKeys = [...planKeys, { key: 'addons', label: 'Add-ons', color: 'var(--viz-4)' }]
  const mix = planKeys.map((k) => ({
    label: k.label,
    value: s.plans.filter((p) => p.plan === k.key).reduce((sum, p) => sum + p.subscriptions, 0),
    color: k.color,
  }))
  const paying = mix.reduce((sum, m) => sum + m.value, 0)
  const yearly = s.plans.filter((p) => p.interval === 'annual').reduce((sum, p) => sum + p.subscriptions, 0)
  const yearlyShare = paying > 0 ? (yearly / paying) * 100 : 0
  const planNames: Record<string, string> = {
    free: 'Free',
    ...Object.fromEntries(s.plans.map((p) => [p.plan, p.name])),
  }

  return (
    <div className={cn('sec transition-opacity', dim && 'opacity-60')} style={{ gap: 20 }}>
      {s.test_mode && (
        <Note icon={<FlaskConical aria-hidden="true" />} title="Billing runs in test mode on this server">
          Test purchases are included in these figures.
        </Note>
      )}
      {s.failed_events > 0 && (
        <Note
          icon={<CircleAlert aria-hidden="true" />}
          title={`${s.failed_events} billing ${s.failed_events === 1 ? 'event needs' : 'events need'} a look`}
        >
          Failed, or still waiting after 15 minutes.{' '}
          <Link to="/subscriptions?tab=events">See billing events</Link>
        </Note>
      )}

      <div className="kpis">
        <KpiTile
          label="Monthly recurring revenue"
          value={formatMoney(s.mrr)}
          now={s.mrr}
          previous={before?.mrr}
          compare={compare}
          spark={months.map((m) => m.mrr)}
        />
        <KpiTile
          label="Annual recurring revenue"
          value={formatMoney(s.arr)}
          compare="yearly plans at their yearly price"
          spark={months.map((m) => m.mrr * 12)}
        />
        <KpiTile
          label="Average per paying account"
          value={formatMoney(s.history.arpa)}
          now={s.history.arpa}
          previous={arpaBefore}
          compare={compare}
          spark={months.map((m) => (m.customers > 0 ? m.mrr / m.customers : 0))}
        />
        <KpiTile
          label="Net revenue kept"
          value={pct(latest?.kept)}
          now={latest?.kept}
          previous={before?.kept}
          points
          compare={
            latest?.kept == null
              ? 'no paying customers at the month’s start'
              : latest.kept >= 100
                ? 'upgrades outweigh losses'
                : 'losses outweigh upgrades'
          }
          spark={months.map((m) => m.kept ?? 0)}
          hint="What the customers paying when the month began pay now, as a share of what they paid then. Upgrades count, so it can pass 100%."
        />
        <KpiTile
          label="Revenue churn"
          value={pct(latest?.churn, 1)}
          now={latest?.churn}
          previous={before?.churn}
          invert
          points
          compare="this month so far, lower is better"
          spark={months.map((m) => m.churn ?? 0)}
          sparkColor="var(--viz-3)"
          hint="Recurring revenue from customers who stopped paying this month, as a share of where the month began"
        />
        <KpiTile
          label="Lifetime value"
          value={s.history.lifetime_value === null ? '—' : formatMoney(s.history.lifetime_value)}
          compare={
            s.history.lifetime_value === null ? 'shown once someone leaves' : 'average per paying customer'
          }
          hint="Average per paying account divided by the monthly share of customers lost (last 3 months)"
        />
      </div>

      <div className="grid2">
        {s.movement && (
          <Panel
            title="This month’s movement"
            sub={`${new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(new Date(`${s.movement.from}T00:00:00`))} to today`}
          >
            <MovementChart
              start={s.movement.start}
              now={s.movement.now}
              startLabel={new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(
                new Date(`${s.movement.from}T00:00:00`),
              )}
              format={formatMoney}
              moves={[
                { label: 'New plans', value: s.movement.new },
                { label: 'Upgrades', value: s.movement.upgrades },
                { label: 'Downgrades', value: s.movement.downgrades },
                { label: 'Cancelled', value: s.movement.cancelled },
              ]}
            />
          </Panel>
        )}
        <Panel
          title="Recurring revenue by plan"
          sub="At the end of each month"
          extra={<Legend keys={stackKeys} />}
        >
          <StackedBars
            rows={months.map((m) => ({
              label: monthLabel(m.month),
              values: Object.fromEntries(stackKeys.map((k) => [k.key, (m.by_plan?.[k.key] ?? 0) / 100])),
            }))}
            keys={stackKeys}
            format={(v) => `$${numberFmt.format(Math.round(v))}`}
            label="Recurring revenue by plan at the end of each month"
          />
        </Panel>
      </div>

      <div className="grid3">
        <Panel title="Plan mix" sub="Paying subscriptions now">
          {paying === 0 ? (
            <p className="empty-line">Nobody pays for a plan yet.</p>
          ) : (
            <div className="donut-wrap">
              <Donut parts={mix} label={numberFmt.format(paying)} sub="paying" />
              <DonutLegend parts={mix} />
            </div>
          )}
        </Panel>
        <Panel title="Billed yearly" sub="Share of paying subscriptions on a yearly plan">
          <div style={{ fontFamily: 'var(--f-display)', fontSize: 44, fontWeight: 500, lineHeight: 1 }}>
            {paying > 0 ? pct(yearlyShare) : '—'}
          </div>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
            {numberFmt.format(yearly)} of the {numberFmt.format(paying)} paying subscriptions pay once a year.
          </p>
          <div style={{ marginTop: 14 }}>
            <Track ratio={yearlyShare / 100} />
          </div>
        </Panel>
        <Panel title="Add-ons" sub="Subscriptions for each">
          {s.addons.length === 0 ? (
            <p className="empty-line">No add-ons sold yet.</p>
          ) : (
            <BarList
              rows={s.addons.map((a) => ({
                key: a.code,
                label: a.name,
                value: a.subscriptions,
                sub: `${formatMoney(a.mrr)}/mo`,
                color: 'var(--viz-4)',
              }))}
              labelWidth={150}
              valueWidth={30}
            />
          )}
        </Panel>
      </div>

      <div className="grid2">
        <Panel
          title="Revenue by market"
          sub="Monthly recurring revenue by where the customer is, from the owner’s time zone"
        >
          <BarList
            rows={(s.markets ?? []).map((m) => ({
              key: m.key,
              label: MARKETS[m.key].label,
              value: m.mrr,
              sub: `${numberFmt.format(m.workspaces)} ${m.workspaces === 1 ? 'customer' : 'customers'}`,
              color: MARKETS[m.key].color,
            }))}
            format={formatMoney}
          />
        </Panel>
        <Panel title="How plans end" sub="Plans that ended or were set to end in the last 90 days">
          {(s.endings ?? []).every((e) => e.count === 0) ? (
            <p className="empty-line">No plan ended in the last 90 days.</p>
          ) : (
            <BarList
              rows={(s.endings ?? [])
                .filter((e) => e.count > 0)
                .map((e) => ({ key: e.key, label: ENDINGS[e.key], value: e.count, color: 'var(--viz-3)' }))}
              labelWidth={230}
            />
          )}
          <p style={{ fontSize: 12.5, color: 'var(--subtle)', marginTop: 12 }}>
            Customers aren’t asked why they cancel yet, so this shows how each plan ended.
          </p>
        </Panel>
      </div>

      {s.one_time && <OneTime oneTime={s.one_time} />}

      <LimitHits hits={s.limit_hits} planNames={planNames} />
      <Payments payments={s.recent_payments} />
      <MonthByMonth s={s} />
    </div>
  )
}

const BATCH_STATES: Record<NonNullable<Sales['one_time']>['lifetime']['state'], string> = {
  off: 'Not on sale (switched off)',
  not_yet: 'Opens for everyone later',
  open: 'On sale now',
  sold_out: 'All places taken',
  closed: 'The batch has closed',
}

/**
 * One-time revenue (lifetime plans and top-ups, never counted as MRR), the founding batch of
 * lifetime plans against its cap, and the lifetime money held and earned at 1/60 a month.
 */
function OneTime({ oneTime }: { oneTime: NonNullable<Sales['one_time']> }) {
  const lt = oneTime.lifetime
  const keys = [
    { key: 'lifetime', label: 'Lifetime plans', color: 'var(--viz-1)' },
    { key: 'top_ups', label: 'Top-ups', color: 'var(--viz-4)' },
  ]
  const total = oneTime.months.reduce((sum, m) => sum + m.total, 0)
  return (
    <div className="grid2">
      <Panel
        title="One-time revenue"
        sub="Lifetime plans and top-ups by month, before tax, refunds taken off. Not part of recurring revenue."
        extra={<Legend keys={keys} />}
      >
        {total === 0 ? (
          <p className="empty-line">No one-time payments in the last 12 months.</p>
        ) : (
          <StackedBars
            rows={oneTime.months.map((m) => ({
              label: monthLabel(m.month),
              values: { lifetime: m.lifetime / 100, top_ups: m.top_ups / 100 },
            }))}
            keys={keys}
            format={(v) => `$${numberFmt.format(Math.round(v))}`}
            label="One-time revenue by month"
          />
        )}
      </Panel>
      <Panel title="Lifetime plans: the founding batch" sub={BATCH_STATES[lt.state]}>
        <div style={{ fontFamily: 'var(--f-display)', fontSize: 44, fontWeight: 500, lineHeight: 1 }}>
          {numberFmt.format(lt.places_taken)}
          <span style={{ fontSize: 18, color: 'var(--muted)' }}> of {numberFmt.format(lt.cap)}</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
          places taken, {numberFmt.format(lt.places_left)} left
          {lt.internal > 0 &&
            ` (${numberFmt.format(lt.internal)} internal test ${lt.internal === 1 ? 'licence' : 'licences'})`}
          . Refunds and revokes free a place.
        </p>
        <div style={{ marginTop: 14 }}>
          <Track ratio={lt.cap > 0 ? lt.places_taken / lt.cap : 0} />
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
          {lt.by_tier.map((t) => (
            <div key={t.plan} className="flex justify-between gap-2">
              <dt className="text-ink-muted">{t.name}</dt>
              <dd className="font-medium text-ink">{numberFmt.format(t.active)}</dd>
            </div>
          ))}
        </dl>
        <dl className="mt-4 grid gap-1.5 border-t border-border pt-3 text-[13px]">
          {[
            ['Paid by lifetime buyers, before tax', lt.money.paid],
            ['Counted as earned (1/60 a month)', lt.money.earned],
            ['Held: keep it in the separate account', lt.money.held],
            ['Most owed if the service closed today', lt.money.most_owed],
            ['Earned this month', lt.money.earned_this_month],
          ].map(([name, value]) => (
            <div key={name} className="flex justify-between gap-3">
              <dt className="text-ink-muted">{name}</dt>
              <dd className="tabular font-medium text-ink">{formatUsd(Number(value))}</dd>
            </div>
          ))}
        </dl>
      </Panel>
    </div>
  )
}

/** Where plans stop people: what they'd need next, and on which plan they were. */
function LimitHits({ hits, planNames }: { hits: Sales['limit_hits']; planNames: Record<string, string> }) {
  if (!hits) return null
  return (
    <section className="sec" aria-labelledby="limit-hits-h">
      <SectionHead
        title={<span id="limit-hits-h">Plan limits people reached</span>}
        small={`the last ${hits.days} days, counted since ${formatDate(`${hits.tracked_since}T00:00:00`)}`}
      />
      {hits.items.length === 0 ? (
        <div className="card pad">
          <p className="empty-line">
            Nobody has reached a plan limit yet. Each time the app stops someone at one, such as a second
            website on Free, it shows here.
          </p>
        </div>
      ) : (
        <div className="tw">
          <table className="t">
            <caption className="sr-only">Plan limits people reached</caption>
            <thead>
              <tr>
                <th>Limit</th>
                <th className="r">People</th>
                <th className="r">Times</th>
                <th>Workspaces by plan</th>
                <th className="r">Last</th>
              </tr>
            </thead>
            <tbody>
              {hits.items.map((h) => (
                <tr key={h.key}>
                  <td>{h.label}</td>
                  <td className="r">{numberFmt.format(h.people)}</td>
                  <td className="r">{numberFmt.format(h.hits)}</td>
                  <td>
                    {Object.entries(h.plans)
                      .map(([plan, n]) => `${planNames[plan] ?? plan} ${numberFmt.format(n)}`)
                      .join(' · ') || '—'}
                  </td>
                  <td className="r">{h.last_at ? formatDate(h.last_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function Payments({ payments }: { payments: Sales['recent_payments'] }) {
  const [filter, setFilter] = useState<PayFilter>('all')
  const rows = payments.filter((p) => payMatches(p.status, filter))
  return (
    <section className="sec" aria-labelledby="payments-h">
      <SectionHead
        title={<span id="payments-h">Payments</span>}
        small="the latest 50"
        extra={
          <div className="bar">
            <Segmented
              label="Status"
              value={filter}
              onChange={setFilter}
              options={PAY_FILTERS.map((f) => ({
                ...f,
                count: payments.filter((p) => payMatches(p.status, f.value)).length,
              }))}
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={rows.length === 0}
              onClick={() => downloadPayments(rows)}
            >
              <Download aria-hidden="true" />
              Export
            </Button>
          </div>
        }
      />
      {rows.length === 0 ? (
        <div className="card pad">
          <p className="empty-line">
            {payments.length === 0
              ? 'No payments yet. They appear here as Lemon Squeezy reports them.'
              : 'No payments with this status.'}
          </p>
        </div>
      ) : (
        <div className="tw">
          <table className="t">
            <caption className="sr-only">Payments</caption>
            <thead>
              <tr>
                <th>Customer</th>
                <th>For</th>
                <th className="r">Amount</th>
                <th>Status</th>
                <th>When</th>
                <th>Country</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.workspace ? (
                      <Link to={`/customers/${p.workspace.id}`}>
                        <b>{p.workspace.name}</b>
                      </Link>
                    ) : (
                      <b>A closed workspace</b>
                    )}
                  </td>
                  <td className="m">
                    {p.description ?? '—'}
                    {p.reason && <small>{label(p.reason)}</small>}
                  </td>
                  <td className="r num">
                    <b>{formatUsd(p.total_usd)}</b>
                    {p.refunded_usd > 0 && <small>{formatUsd(p.refunded_usd)} refunded</small>}
                  </td>
                  <td>
                    <span
                      className={cn(
                        'chip',
                        p.status === 'paid'
                          ? 'ok'
                          : p.status === 'refunded' || p.status === 'partial_refund'
                            ? ''
                            : 'bad',
                      )}
                    >
                      {label(p.status)}
                    </span>
                    {p.test_mode && (
                      <span className="chip" style={{ marginLeft: 4 }}>
                        Test
                      </span>
                    )}
                  </td>
                  <td className="m">{formatDate(p.issued_at)}</td>
                  <td>{p.country ? <CountryCode code={p.country} /> : <span className="m">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/** The month-by-month tables: recurring revenue and customers, and what was collected. */
function MonthByMonth({ s }: { s: Sales }) {
  const [tab, setTab] = useState<'recurring' | 'collected'>('recurring')
  return (
    <section className="sec" aria-labelledby="months-h">
      <SectionHead title={<span id="months-h">Month by month</span>} />
      <Tabs
        label="Month by month"
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'recurring', label: 'Recurring revenue' },
          { key: 'collected', label: 'Collected' },
        ]}
      />
      {tab === 'recurring' ? (
        <>
          <div className="tw">
            <table className="t">
              <caption className="sr-only">Recurring revenue by month</caption>
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="r">MRR</th>
                  <th className="r">Customers</th>
                  <th className="r">New</th>
                  <th className="r">Lost</th>
                  <th className="r">Revenue kept</th>
                  <th className="r">Collected</th>
                  <th className="r">Discounts</th>
                  <th className="r">Net</th>
                </tr>
              </thead>
              <tbody>
                {[...s.history.months].reverse().map((m) => (
                  <tr key={m.month}>
                    <td>
                      <b>{longMonth(m.month)}</b>
                    </td>
                    <td className="r num">{formatUsd(m.mrr)}</td>
                    <td className="r num">{numberFmt.format(m.customers)}</td>
                    <td className="r num">{numberFmt.format(m.new_customers)}</td>
                    <td className="r num">{numberFmt.format(m.lost_customers)}</td>
                    <td className="r num">{pct(m.kept)}</td>
                    <td className="r num">{formatUsd(m.collected)}</td>
                    <td className="r num">{formatUsd(m.discounts)}</td>
                    <td className="r num">{formatUsd(m.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--subtle)' }}>
            Each month’s recurring revenue values each subscription at what it paid by then (its catalog price
            before a first payment), so older months are estimates. Money collected comes straight from the
            payments.
          </p>
        </>
      ) : (
        <>
          <div className="tw">
            <table className="t">
              <caption className="sr-only">Collected by month</caption>
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="r">Payments</th>
                  <th className="r">Collected</th>
                  <th className="r">Tax</th>
                  <th className="r">Refunded</th>
                  <th className="r">Net</th>
                  <th className="r">Est. fees</th>
                </tr>
              </thead>
              <tbody>
                {[...s.months].reverse().map((m) => (
                  <tr key={m.month}>
                    <td>
                      <b>{longMonth(m.month)}</b>
                    </td>
                    <td className="r num">{numberFmt.format(m.payments)}</td>
                    <td className="r num">{formatUsd(m.collected)}</td>
                    <td className="r num">{formatUsd(m.tax)}</td>
                    <td className="r num">{formatUsd(m.refunded)}</td>
                    <td className="r num">{formatUsd(m.net)}</td>
                    <td className="r num">{formatUsd(m.estimated_fees)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--subtle)' }}>
            Paid charges in USD, the last 12 months. Net leaves out tax and refunds; fees are estimated at
            5.5% + 50¢ a charge (more for international cards and PayPal), and the exact figures are in Lemon
            Squeezy’s payouts. All time: {formatUsd(s.history.lifetime_collected)} collected.
          </p>
        </>
      )}
    </section>
  )
}

function downloadPayments(rows: Sales['recent_payments']) {
  const cell = (v: string | number | null) => {
    const t = v == null ? '' : String(v)
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t
  }
  const lines = [
    ['Date', 'Customer', 'For', 'Reason', 'Status', 'Amount (USD)', 'Refunded (USD)', 'Country', 'Test'],
    ...rows.map((p) => [
      p.issued_at?.slice(0, 10) ?? '',
      p.workspace?.name ?? '',
      p.description ?? '',
      p.reason ?? '',
      p.status,
      (p.total_usd / 100).toFixed(2),
      (p.refunded_usd / 100).toFixed(2),
      p.country ?? '',
      p.test_mode ? 'yes' : '',
    ]),
  ]
  const blob = new Blob([lines.map((l) => l.map(cell).join(',')).join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'arva-seo-payments.csv'
  a.click()
  URL.revokeObjectURL(url)
}
